import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import SelectAutoComplete from 'atoms/SelectAutoComplete';

/**
 * EditSessionModal
 *
 * Allows setting a covering clinician for one or more sessions
 */
const EditSessionModal = ({ hide, sessions, clinicians, onSubmit, groupName, isLoading }) => {
	const [selectedClinician, setSelectedClinician] = useState(null);

	const coverageInfo = (() => {
		const covered = sessions.filter((s) => s.clinicianId);
		if (covered.length === 0) return null;
		if (sessions.length === 1) {
			const match = clinicians.find((c) => c.id === covered[0].clinicianId);
			return match ? `Currently covered by ${match.name}` : 'Coverage assigned';
		}
		return `${covered.length} of ${sessions.length} session(s) have coverage assigned`;
	})();

	const handleClinicianChange = (option) => {
		setSelectedClinician(option);
	};

	const handleSubmit = () => {
		onSubmit({
			sessionIds: sessions.map(s => s.sessionId),
			clinicianId: selectedClinician?.value || null,
		});
	};

	const handleClearCoverage = () => {
		setSelectedClinician(null);
	};

	const renderSessionSummary = () => {
		if (sessions.length === 1) {
			const session = sessions[0];
			const sessionDate = moment.utc(session.startDate).tz('America/Los_Angeles').format('MMMM D, YYYY');
			const startTime = moment.utc(session.startDate).tz('America/Los_Angeles').format('h:mm A');
			const endTime = moment.utc(session.endDate).tz('America/Los_Angeles').format('h:mm A');
			const timezone = moment().tz('America/Los_Angeles').format('z');
			return (
				<>
					<div className="font-14">{sessionDate}</div>
					<div className="font-14 text-muted">{startTime} - {endTime} {timezone}</div>
				</>
			);
		}
		return (
			<div className="font-14">{sessions.length} sessions selected</div>
		);
	};

	return (
		<Modal
			isOpen={true}
			className="modal-dialog-centered"
			backdrop="static"
		>
			<ModalHeader toggle={hide} className="border-0">
				Set Session Coverage
			</ModalHeader>
			<ModalBody className="container-fluid pt-0">
				<div className="mb-3">
					<div className="text-muted font-10 text-uppercase">
						{sessions.length === 1 ? 'Session' : 'Sessions'}
					</div>
					<div className="font-16">{groupName}</div>
					{renderSessionSummary()}
				</div>

				{coverageInfo && (
					<div className="mb-2 font-12 text-info">
						<i className="fas fa-info-circle mr-1" />{coverageInfo}
					</div>
				)}

				<div className="form-group">
					<label className="text-uppercase text-muted font-10">
						Covering Clinician
					</label>
					<SelectAutoComplete
						handleChange={handleClinicianChange}
						options={clinicians.map(({ id, name, therapistType }) => ({
							value: id,
							label: name,
							subLabel: therapistType,
						}))}
						value={selectedClinician || undefined}
						placeholder="Enter or select clinician"
						isClearable
					/>
					<small className="text-muted">
						Leave empty to use the group&apos;s assigned facilitators
					</small>
				</div>

				{selectedClinician && (
					<div className="mb-3">
						<button
							type="button"
							className="btn btn-link text-danger p-0 font-12"
							onClick={handleClearCoverage}
						>
							Clear coverage (use default facilitators)
						</button>
					</div>
				)}

				<div className="d-flex justify-content-center align-items-center w-100 p-4 mb-3">
					<button
						type="button"
						className="btn btn-primary btn-lg btn-block btn-color text-uppercase font-13 col-4 d-flex justify-content-center"
						onClick={handleSubmit}
						disabled={isLoading}
					>
						<span className="mr-0 w-100">{isLoading ? 'SAVING...' : 'SUBMIT'}</span>
						<i className="fas fa-check align-self-center" />
					</button>
				</div>
			</ModalBody>
		</Modal>
	);
};

EditSessionModal.propTypes = {
	hide: PropTypes.func.isRequired,
	sessions: PropTypes.arrayOf(PropTypes.shape({
		sessionId: PropTypes.number.isRequired,
		startDate: PropTypes.string.isRequired,
		endDate: PropTypes.string.isRequired,
		clinicianId: PropTypes.number,
	})).isRequired,
	clinicians: PropTypes.array.isRequired,
	onSubmit: PropTypes.func.isRequired,
	groupName: PropTypes.string,
	isLoading: PropTypes.bool,
};

export default EditSessionModal;
